import { EditOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import {
  productStatusColors,
  productStatusLabels,
} from 'apps/investcore/constants/fundProduct';
import { etfProfileDetail } from 'apps/investcore/apiCalls/etf-products/etf-profile';
import InfoCard from 'common/ui/info-card';
import { useFetchApiGet } from 'hooks/api';
import {
  BuildingIcon,
  ChartBarIcon,
  ExternalLinkIcon,
  TagIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDate, formatDateTime } from 'utils/formatDate';
import { cloudflareCdn } from 'utils/image';

const SectionHeader = ({ icon, title, editUrl, editLabel }) => (
  <div className="flex items-center justify-between pt-2 border-b pb-1">
    <h3 className="text-base font-medium text-gray-800 flex items-center gap-1">
      {icon}
      {title}
    </h3>
    <Link to={editUrl} className="flex items-center gap-1 text-sm">
      <EditOutlined /> {editLabel}
    </Link>
  </div>
);

const ItemDetail = ({ item }) => {
  const {
    id,
    created_at,
    updated_at,
    symbol,
    name,
    exchange,
    description,
    logo,
    status,
    fund,
    fund_manager_name,
    ranking,
    icb_industry_code,
    icb_industry,
    icb_supersector_code,
    icb_supersector,
    icb_sector_code,
    icb_sector,
    icb_subsector_code,
    icb_subsector,
    estimated_min_price,
    estimated_max_price,
    estimated_avg_price,
    estimated_price_updated_at,
    price,
    price_date,
    price_last_updated,
  } = item || {};

  // ETF-specific fields live on the ETF profile (etf_profiles), which extends the
  // stock. Fetch it and prefer its values over the deprecated stock columns.
  const { data: etfProfile, load: loadEtfProfile } = useFetchApiGet(
    etfProfileDetail,
    { resourceName: 'data' }
  );

  useEffect(() => {
    if (id) loadEtfProfile({ id });
  }, [id]);

  const profile = etfProfile || {};
  const market_index_symbol =
    profile.market_index_symbol ?? item?.market_index_symbol;
  const inception_date = profile.inception_date ?? item?.inception_date;
  const audit_company = profile.audit_company ?? item?.audit_company;
  const strategy = profile.strategy ?? item?.strategy;
  const management_fee = profile.management_fee ?? item?.management_fee;
  const trading_fee = profile.trading_fee ?? item?.trading_fee;
  const trading_schedule = profile.trading_schedule ?? item?.trading_schedule;
  const trading_method = profile.trading_method ?? item?.trading_method;
  const prospectus_url = profile.prospectus_url ?? item?.prospectus_url;
  const supervisory_bank = profile.supervisory_bank ?? item?.supervisory_bank;

  // Fund manager now lives on the ETF profile (fallback to the deprecated stock
  // fields during the transition).
  const fundManagerFund = profile.fund ?? fund;
  const fundManagerName = profile.fund_manager_name ?? fund_manager_name;
  const fundManagerDisplay = fundManagerFund
    ? {
        name: `${fundManagerFund.name} (${fundManagerFund.code})`,
        id: fundManagerFund.id,
      }
    : { name: fundManagerName, id: null };

  const editStockUrl = `/admin/products/etfs/${id}/edit`;
  const editProfileUrl = `/admin/products/etfs/${id}/edit-profile`;

  return (
    <div className="space-y-4">
      {/* ============ Stock Information ============ */}
      <SectionHeader
        icon={<TagIcon size={18} />}
        title="Stock Information"
        editUrl={editStockUrl}
        editLabel="Edit Stock"
      />

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex gap-4">
          {logo ? (
            <img
              src={cloudflareCdn(logo, 100)}
              alt="Logo"
              className="w-14 rounded object-contain"
            />
          ) : null}
          <InfoCard className="flex-grow" label="Symbol" value={symbol} />
        </div>
        <InfoCard className="flex-grow" label="Name" value={name} />
        <InfoCard
          className="flex-grow"
          label="Status"
          value={
            <Tag color={productStatusColors[status]}>
              {productStatusLabels[status] || status}
            </Tag>
          }
        />
        <InfoCard className="flex-grow" label="Exchange" value={exchange} />
        <InfoCard label="Ranking" value={formatNumber(ranking)} />
      </div>

      <InfoCard label="Description" value={description} />

      {/* ICB Classification */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b flex items-center gap-1">
        <TagIcon size={16} />
        ICB Classification
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard
          label="Industry (L1)"
          value={
            icb_industry_code ? (
              <Link
                to={`/admin/stock-icb/industries/${icb_industry_code}`}
                className="flex flex-col gap-0.5"
              >
                <Tag color="blue" className="w-fit">
                  {icb_industry_code}
                </Tag>
                <span className="text-sm">{icb_industry?.name ?? ''}</span>
              </Link>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="SuperSector (L2)"
          value={
            icb_supersector_code ? (
              <Link
                to={`/admin/stock-icb/supersectors/${icb_supersector_code}`}
                className="flex flex-col gap-0.5"
              >
                <Tag color="cyan" className="w-fit">
                  {icb_supersector_code}
                </Tag>
                <span className="text-sm">{icb_supersector?.name ?? ''}</span>
              </Link>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="Sector (L3)"
          value={
            icb_sector_code ? (
              <Link
                to={`/admin/stock-icb/sectors/${icb_sector_code}`}
                className="flex flex-col gap-0.5"
              >
                <Tag color="green" className="w-fit">
                  {icb_sector_code}
                </Tag>
                <span className="text-sm">{icb_sector?.name ?? ''}</span>
              </Link>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="SubSector (L4)"
          value={
            icb_subsector_code ? (
              <Link
                to={`/admin/stock-icb/subsectors/${icb_subsector_code}`}
                className="flex flex-col gap-0.5"
              >
                <Tag color="orange" className="w-fit">
                  {icb_subsector_code}
                </Tag>
                <span className="text-sm">{icb_subsector?.name ?? ''}</span>
              </Link>
            ) : (
              '-'
            )
          }
        />
      </div>

      {/* Pricing */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b">
        Pricing
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="Price" value={formatCurrency(price)} />
        <InfoCard label="Price date" value={formatDate(price_date)} />
        <InfoCard
          label="Price last updated"
          value={formatDateTime(price_last_updated)}
        />
      </div>

      {/* Estimated Prices */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b">
        Estimated Prices
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard
          label="Estimated Min Price"
          value={formatCurrency(estimated_min_price)}
        />
        <InfoCard
          label="Estimated Max Price"
          value={formatCurrency(estimated_max_price)}
        />
        <InfoCard
          label="Estimated Avg Price"
          value={formatCurrency(estimated_avg_price)}
        />
        <InfoCard
          label="Estimated Price Updated"
          value={formatDateTime(estimated_price_updated_at)}
        />
      </div>

      {/* ============ ETF Profile ============ */}
      <SectionHeader
        icon={<ChartBarIcon size={18} />}
        title="ETF Profile"
        editUrl={editProfileUrl}
        editLabel="Edit Profile"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard
          label="Fund Manager"
          value={
            fundManagerDisplay.id ? (
              <Link to={`/admin/fund-managers/${fundManagerDisplay.id}`}>
                {fundManagerDisplay.name}
              </Link>
            ) : (
              fundManagerDisplay.name || '-'
            )
          }
        />
        <InfoCard
          label="Market Index Symbol"
          value={market_index_symbol || '-'}
        />
        <InfoCard label="Inception Date" value={formatDate(inception_date)} />
        <InfoCard label="Audit Company" value={audit_company} />
      </div>

      <InfoCard label="Strategy" value={strategy} />

      {/* Trading */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b">
        Trading
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard label="Trading Schedule" value={trading_schedule} />
        <InfoCard label="Trading Method" value={trading_method} />
        <InfoCard label="Management Fee" value={formatCurrency(management_fee)} />
        <InfoCard label="Trading Fee" value={formatCurrency(trading_fee)} />
      </div>

      {/* Bank */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b flex items-center gap-1">
        <BuildingIcon size={16} />
        Bank
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard
          label="Supervisory Bank"
          value={
            supervisory_bank ? (
              <Link
                to={`/admin/banks/${supervisory_bank?.id}`}
              >{`${supervisory_bank?.name} - ${supervisory_bank?.code}`}</Link>
            ) : (
              '-'
            )
          }
        />
      </div>

      {/* Documents */}
      <h4 className="text-sm font-medium text-gray-600 pt-2 border-b">
        Documents
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard
          label="Prospectus URL"
          value={
            prospectus_url ? (
              <a
                href={prospectus_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                View Prospectus
              </a>
            ) : (
              '-'
            )
          }
        />
      </div>

      {/* Metadata */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Created At" value={formatDateTime(created_at)} />
          <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
